/**
 * Opnemen in de browser.
 *
 * Losse logica naast het scherm, zoals `upload.ts`: het scherm gaat over knoppen
 * en de lopende tijd, hier staat wat er met de microfoon gebeurt.
 *
 * Wat eruit komt is een `File`, en die gaat langs precies dezelfde weg naar R2
 * als een gesleept bestand. Verderop in de keten is er geen verschil tussen een
 * opname en een upload, en dat hoort ook zo: `mediaService.normaliseer` zet elk
 * bestand toch om naar mono 16 kHz Opus.
 */

import { bewaarStuk, sluitOpnameAf, startOpslag, vindOnafgemaakteOpname } from './opnameOpslag';

/**
 * Op volgorde van voorkeur. Chrome en Firefox leveren WebM/Opus, Safari tot
 * iOS 18.3 alleen MP4/AAC. Niet één type hardcoderen dus: dat is precies hoe je
 * op één apparaat een leeg bestand krijgt.
 *
 * De lege string als laatste is "kies zelf maar": dan bepaalt de browser het,
 * en dat is nog altijd beter dan niet opnemen.
 */
export const TYPE_VOORKEUR = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/aac',
  '',
] as const;

/** Spraak, geen muziek. 32 kbps mono houdt drie kwartier onder de 15 MB, en dat
 *  scheelt zowel geheugen tijdens de opname als tijd bij het uploaden. */
export const BITRATE = 32_000;

/** Elke tien seconden een stuk. Klein genoeg om weinig kwijt te zijn bij een
 *  crash, groot genoeg om de opslag niet te bestoken. */
export const STUK_MS = 10_000;

export function kiesType(
  ondersteund: (type: string) => boolean = (t) =>
    typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t),
): string {
  return TYPE_VOORKEUR.find((t) => t === '' || ondersteund(t)) ?? '';
}

/** De extensie hoort bij wat er werkelijk in het bestand zit; ffmpeg kijkt naar
 *  de inhoud, maar een bestand dat `.webm` heet en mp4 is verwart iedereen. */
export function extensieVoor(type: string): string {
  if (type.includes('webm')) return 'webm';
  if (type.includes('mp4') || type.includes('aac') || type.includes('m4a')) return 'm4a';
  if (type.includes('ogg')) return 'ogg';
  return 'webm';
}

/** mm:ss, en uu:mm:ss zodra het een uur wordt. */
export function tijdTekst(seconden: number): string {
  const s = Math.max(0, Math.floor(seconden));
  const uren = Math.floor(s / 3600);
  const rest = s % 3600;
  const mm = String(Math.floor(rest / 60)).padStart(2, '0');
  const ss = String(rest % 60).padStart(2, '0');
  return uren > 0 ? `${uren}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function bestandsnaam(begonnenOp: Date, type: string): string {
  const d = begonnenOp;
  const twee = (n: number) => String(n).padStart(2, '0');
  const stempel = `${d.getFullYear()}-${twee(d.getMonth() + 1)}-${twee(d.getDate())}-${twee(d.getHours())}${twee(d.getMinutes())}`;
  return `opname-${stempel}.${extensieVoor(type)}`;
}

export const kanOpnemen = () =>
  typeof MediaRecorder !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  Boolean(navigator.mediaDevices?.getUserMedia);

export interface OpnameSessie {
  /** Voor het terughalen na een crash. */
  id: string;
  pauzeer: () => void;
  hervat: () => void;
  /** Stopt, sluit de microfoon en levert het bestand. */
  stop: () => Promise<File>;
  /** Stoppen zonder bestand: alles weg, ook uit de opslag. */
  afbreken: () => Promise<void>;
}

export interface OpnameHandlers {
  /** Elke seconde, met de opgenomen tijd in seconden. */
  onTijd?: (seconden: number) => void;
  /** Uitslag van de meter, 0 tot 1. Voor het scherm, niet voor de opname. */
  onNiveau?: (niveau: number) => void;
  /** De opname is buiten ons om gestopt; op een telefoon gebeurt dat als het
   *  toestel de microfoon opeist voor een telefoontje. */
  onOnderbroken?: (melding: string) => void;
}

/**
 * Begint met opnemen. Vraagt zelf om toegang tot de microfoon; weigert de
 * gebruiker dat, dan komt de fout van `getUserMedia` naar boven.
 */
export async function startOpname(handlers: OpnameHandlers = {}): Promise<OpnameSessie> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      // Een laptopmicrofoon tussen twee mensen: de buddy hard, de deelnemer
      // zacht. Dit haalt het ergste eraf; het normaliseren in de container doet de
      // rest, maar die krijgt hierdoor beter materiaal.
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });

  const type = kiesType();
  const recorder = new MediaRecorder(stream, {
    ...(type ? { mimeType: type } : {}),
    audioBitsPerSecond: BITRATE,
  });
  const echteType = recorder.mimeType || type || 'audio/webm';
  const begonnenOp = new Date();

  const id = await startOpslag({ type: echteType, begonnenOp: begonnenOp.toISOString() });

  const stukken: Blob[] = [];
  let nummer = 0;
  recorder.ondataavailable = (e) => {
    if (e.data.size === 0) return;
    stukken.push(e.data);
    // Ook naar de opslag, zodat een crash niet het hele gesprek kost. Faalt dat
    // (geen ruimte, privémodus), dan gaat de opname gewoon door: het vangnet mag
    // nooit de opname zelf zijn.
    void bewaarStuk(id, nummer++, e.data).catch((err) =>
      console.warn('[opname] stuk niet bewaard', err),
    );
  };

  // De meter. Los van de opname: dit leest de stroom mee, het verandert hem niet.
  const audio = new AudioContext();
  const bron = audio.createMediaStreamSource(stream);
  const meter = audio.createAnalyser();
  meter.fftSize = 512;
  bron.connect(meter);
  const buffer = new Uint8Array(meter.frequencyBinCount);

  let gestopt = false;
  const tik = window.setInterval(() => {
    if (gestopt) return;
    if (recorder.state === 'recording') {
      handlers.onTijd?.(verstreken());
      meter.getByteTimeDomainData(buffer);
      let piek = 0;
      for (const v of buffer) piek = Math.max(piek, Math.abs(v - 128) / 128);
      handlers.onNiveau?.(piek);
    }
  }, 200);

  /** Zelf bijhouden en niet aftrekken van de starttijd: pauzeren telt niet mee,
   *  en dan zou de klok verder lopen dan de opname duurt. */
  let opgenomenMs = 0;
  let laatsteStart = Date.now();
  const verstreken = () =>
    (opgenomenMs + (recorder.state === 'recording' ? Date.now() - laatsteStart : 0)) / 1000;

  stream.getAudioTracks().forEach((track) => {
    track.onended = () => {
      if (!gestopt) handlers.onOnderbroken?.('De microfoon is niet meer beschikbaar.');
    };
  });

  function ruimOp() {
    gestopt = true;
    window.clearInterval(tik);
    stream.getTracks().forEach((t) => t.stop());
    void audio.close().catch(() => {});
  }

  recorder.start(STUK_MS);

  return {
    id,
    pauzeer() {
      if (recorder.state !== 'recording') return;
      opgenomenMs += Date.now() - laatsteStart;
      recorder.pause();
    },
    hervat() {
      if (recorder.state !== 'paused') return;
      laatsteStart = Date.now();
      recorder.resume();
    },
    stop() {
      return new Promise<File>((klaar, mislukt) => {
        recorder.onerror = (e) => {
          ruimOp();
          mislukt(e instanceof Error ? e : new Error('De opname is mislukt.'));
        };
        recorder.onstop = () => {
          ruimOp();
          const blob = new Blob(stukken, { type: echteType });
          void sluitOpnameAf(id).catch(() => {});
          klaar(new File([blob], bestandsnaam(begonnenOp, echteType), { type: echteType }));
        };
        if (recorder.state === 'recording') opgenomenMs += Date.now() - laatsteStart;
        recorder.stop();
      });
    },
    async afbreken() {
      if (recorder.state !== 'inactive') recorder.stop();
      ruimOp();
      await sluitOpnameAf(id).catch(() => {});
    },
  };
}

/**
 * Een opname die is blijven liggen na een crash, als bestand.
 *
 * De stukken staan er nog, alleen niet aan elkaar. Ze in volgorde samenvoegen
 * levert hetzelfde bestand op als een normale stop; alleen het laatste stuk van
 * hooguit tien seconden ontbreekt.
 */
export async function herstelOpname(): Promise<{ id: string; bestand: File } | null> {
  const gevonden = await vindOnafgemaakteOpname();
  if (!gevonden) return null;
  const begonnenOp = new Date(gevonden.begonnenOp);
  return {
    id: gevonden.id,
    bestand: new File([gevonden.blob], bestandsnaam(begonnenOp, gevonden.type), {
      type: gevonden.type,
    }),
  };
}
